/**
 * PNG minimo com payload no bit menos significativo dos canais RGB.
 *
 * Escrito a mao, sem dependencia nativa, porque o mesmo codigo roda no
 * aparelho (React Native, sem zlib) e no Node. O fluxo zlib usa **blocos
 * armazenados** (BTYPE=00), que sao zlib validos e nao exigem um compressor:
 * o payload e ciphertext, ou seja incompressivel de qualquer jeito.
 *
 * A imagem base e ruido colorido de `randombytes_buf`. Como os 7 bits de
 * cima ja sao aleatorios, o LSB carregando dados nao muda nada que a vista
 * ou uma analise estatistica simples consigam separar.
 *
 * SO SOBREVIVE COMO DOCUMENTO. Enviado como foto, o mensageiro recomprime
 * para JPEG e o LSB desaparece inteiro.
 */
import { ascii, concatBytes, readU32BE, requireRange, tagAt, u32be, view } from './binary.js'
import { fromBits, stripLengthPrefix, toBits, withLengthPrefix } from './bitstream.js'
import { adler32, crc32 } from './checksums.js'
import { randomBytes } from './random.js'
import { DisguiseError } from './types.js'

const SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
const CHANNELS = 3 // RGB truecolor, 8 bits por canal
const MAX_STORED_BLOCK = 0xffff
const IHDR_BYTES = 13

function chunk(type: string, data: Uint8Array): Uint8Array {
  const body = concatBytes([ascii(type), data])
  return concatBytes([u32be(data.length), body, u32be(crc32(body))])
}

/** Fluxo zlib com blocos armazenados — valido e sem compressor. */
function zlibStored(raw: Uint8Array): Uint8Array {
  const blocks: Uint8Array[] = []
  let offset = 0
  do {
    const slice = raw.subarray(offset, offset + MAX_STORED_BLOCK)
    const isLast = offset + MAX_STORED_BLOCK >= raw.length
    const header = new Uint8Array(5)
    const dv = view(header)
    dv.setUint8(0, isLast ? 1 : 0)
    dv.setUint16(1, slice.length, true)
    dv.setUint16(3, ~slice.length & 0xffff, true)
    blocks.push(concatBytes([header, slice]))
    offset += MAX_STORED_BLOCK
  } while (offset < raw.length)

  // 0x78 0x01: CM=8, CINFO=7, sem dicionario.
  return concatBytes([new Uint8Array([0x78, 0x01]), ...blocks, u32be(adler32(raw))])
}

function inflateStored(stream: Uint8Array): Uint8Array {
  requireRange(stream, 0, 6)
  if (view(stream).getUint8(0) !== 0x78) throw new DisguiseError('fluxo zlib inesperado no PNG')

  const parts: Uint8Array[] = []
  let pos = 2
  for (;;) {
    requireRange(stream, pos, 5)
    const dv = view(stream)
    const header = dv.getUint8(pos)
    if ((header & 0x06) !== 0) {
      // So geramos blocos armazenados; comprimido veio de fora.
      throw new DisguiseError('PNG nao foi gerado pelo CriptoHomer (bloco comprimido)')
    }
    const length = dv.getUint16(pos + 1, true)
    requireRange(stream, pos + 5, length)
    parts.push(stream.subarray(pos + 5, pos + 5 + length))
    pos += 5 + length
    if ((header & 1) === 1) break
  }
  return concatBytes(parts)
}

export function isPng(bytes: Uint8Array): boolean {
  if (bytes.length < SIGNATURE.length) return false
  const dv = view(bytes)
  for (let i = 0; i < SIGNATURE.length; i++) {
    if (dv.getUint8(i) !== SIGNATURE[i]) return false
  }
  return true
}

export function encodePng(payload: Uint8Array): Uint8Array {
  const bits = toBits(withLengthPrefix(payload))
  const pixelsNeeded = Math.ceil(bits.length / CHANNELS)
  const width = Math.max(8, Math.ceil(Math.sqrt(pixelsNeeded)))
  const height = Math.max(1, Math.ceil(pixelsNeeded / width))

  // Ruido colorido: 7 bits de cima aleatorios, o de baixo carrega o dado.
  // Os LSBs que sobram levam ruido do mesmo buffer, nao um padrao fixo.
  const channels = width * height * CHANNELS
  const noise = randomBytes(channels)
  const filler = randomBytes(channels)
  const noiseView = view(noise)
  const fillerView = view(filler)
  for (let i = 0; i < channels; i++) {
    const bit = i < bits.length ? (bits[i] ?? 0) : fillerView.getUint8(i) & 1
    noiseView.setUint8(i, (noiseView.getUint8(i) & 0xfe) | bit)
  }

  // Scanlines com filtro 0 (None): o dado ja e aleatorio, filtrar nao ajuda.
  const stride = width * CHANNELS
  const raw = new Uint8Array(height * (1 + stride))
  for (let y = 0; y < height; y++) {
    raw.set(noise.subarray(y * stride, (y + 1) * stride), y * (1 + stride) + 1)
  }

  const ihdr = new Uint8Array(IHDR_BYTES)
  const ihdrView = view(ihdr)
  ihdrView.setUint32(0, width, false)
  ihdrView.setUint32(4, height, false)
  ihdrView.setUint8(8, 8) // profundidade de bits
  ihdrView.setUint8(9, 2) // truecolor RGB

  return concatBytes([
    SIGNATURE,
    chunk('IHDR', ihdr),
    chunk('IDAT', zlibStored(raw)),
    chunk('IEND', new Uint8Array(0)),
  ])
}

export function decodePng(bytes: Uint8Array): Uint8Array {
  if (!isPng(bytes)) throw new DisguiseError('nao e um PNG')

  let pos = SIGNATURE.length
  let width = 0
  let height = 0
  const idat: Uint8Array[] = []

  while (pos + 8 <= bytes.length) {
    const length = readU32BE(bytes, pos)
    const type = tagAt(bytes, pos + 4)
    requireRange(bytes, pos + 8, length + 4)
    const data = bytes.subarray(pos + 8, pos + 8 + length)

    if (type === 'IHDR') {
      requireRange(data, 0, IHDR_BYTES)
      width = readU32BE(data, 0)
      height = readU32BE(data, 4)
      const dv = view(data)
      if (dv.getUint8(8) !== 8 || dv.getUint8(9) !== 2) {
        throw new DisguiseError('PNG nao e RGB de 8 bits sem entrelacamento')
      }
    } else if (type === 'IDAT') {
      idat.push(data)
    } else if (type === 'IEND') {
      break
    }
    pos += 8 + length + 4
  }

  if (width === 0 || height === 0 || idat.length === 0) throw new DisguiseError('PNG sem imagem')

  const raw = inflateStored(concatBytes(idat))
  const stride = width * CHANNELS
  requireRange(raw, 0, height * (1 + stride))

  const rawView = view(raw)
  const bits = new Uint8Array(width * height * CHANNELS)
  let bitIndex = 0
  for (let y = 0; y < height; y++) {
    const rowStart = y * (1 + stride) + 1
    for (let i = 0; i < stride; i++) bits[bitIndex++] = rawView.getUint8(rowStart + i) & 1
  }
  return stripLengthPrefix(fromBits(bits))
}
